import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

// Redeem a ?sso=<code> param left by another Bluebird Suite app (BBD-50). Single-use, so the
// param is stripped from the URL immediately, before the network call resolves.
const trySSOSignIn = async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('sso');
    if (!code) return false;

    params.delete('sso');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash);

    try {
        const res = await fetch(`${API_BASE}/users/sso/redeem/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
        });
        if (!res.ok) return false;
        const { custom_token } = await res.json();
        await signInWithCustomToken(auth, custom_token);
        return true;
    } catch (err) {
        console.error('SSO sign-in failed:', err);
        return false;
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(null);
    const [emailVerified, setEmailVerified] = useState(false);
    const ssoCodeRef = useRef({ code: null, expiresAt: 0 });
    const ssoInflightRef = useRef(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!firebaseUser) {
                // No local session — see if another suite app just handed us a sign-in code
                // before giving up and rendering the logged-out state.
                const signedIn = await trySSOSignIn();
                if (signedIn) return; // onAuthStateChanged fires again with the new user
            }

            if (firebaseUser) {
                // Reset loading to true while processing auth state
                // Prevents race condition where loading=false (from previous null state)
                // allows ProtectedRoute to evaluate with stale emailVerified=false
                setLoading(true);

                const idToken = await firebaseUser.getIdToken();
                setUser(firebaseUser);
                setToken(idToken);
                setEmailVerified(firebaseUser.emailVerified);

                // Ensure user exists in Django backend
                // Small delay to avoid "Token used too early" clock skew errors
                // with freshly minted tokens right after sign-in
                try {
                    const checkResponse = await fetch(`${API_BASE}/users/${firebaseUser.uid}/`, {
                        headers: { 'Authorization': idToken }
                    });

                    if (checkResponse.status === 404) {
                        await fetch(`${API_BASE}/users/ensure-exists/`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': idToken,
                            },
                            body: JSON.stringify({
                                email: firebaseUser.email,
                                name: firebaseUser.displayName || '',
                            }),
                        });
                    } else if (checkResponse.status === 401 || checkResponse.status === 403) {
                        // Token may be too fresh (clock skew) — retry once after delay
                        const body = await checkResponse.json().catch(() => ({}));
                        if (body.details && body.details.includes('Token used too early')) {
                            await new Promise(resolve => setTimeout(resolve, 3000));
                            const freshToken = await firebaseUser.getIdToken(true);
                            setToken(freshToken);
                            const retryResponse = await fetch(`${API_BASE}/users/${firebaseUser.uid}/`, {
                                headers: { 'Authorization': freshToken }
                            });
                            if (retryResponse.status === 404) {
                                await fetch(`${API_BASE}/users/ensure-exists/`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': freshToken,
                                    },
                                    body: JSON.stringify({
                                        email: firebaseUser.email,
                                        name: firebaseUser.displayName || '',
                                    }),
                                });
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error syncing user with backend:', err);
                }
            } else {
                setUser(null);
                setToken(null);
                setEmailVerified(false);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const getToken = useCallback(async () => {
        if (auth.currentUser) {
            const idToken = await auth.currentUser.getIdToken();
            setToken(idToken);
            return idToken;
        }
        return null;
    }, []);

    const apiCall = useCallback(async (path, options = {}, _retried = false) => {
        const idToken = await getToken();
        if (!idToken) throw new Error('Not authenticated');

        // For FormData (file uploads) let the browser set the multipart
        // Content-Type (with boundary) — forcing application/json breaks uploads.
        const isFormData = options.body instanceof FormData;
        const baseHeaders = {
            'Authorization': idToken,
            'Requesting-User-Uid': auth.currentUser.uid,
        };
        if (!isFormData) {
            baseHeaders['Content-Type'] = 'application/json';
        }

        const response = await fetch(`${API_BASE}${path}`, {
            ...options,
            headers: {
                ...baseHeaders,
                ...options.headers,
            },
        });

        // Handle clock skew: "Token used too early" from backend
        // Retry once after a short delay with a refreshed token
        if (!_retried && (response.status === 401 || response.status === 403)) {
            try {
                const body = await response.clone().json();
                if (body.details && body.details.includes('Token used too early')) {
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    return apiCall(path, options, true);
                }
            } catch {
                // If response isn't JSON, fall through and return original response
            }
        }

        return response;
    }, [getToken]);

    const refreshEmailVerified = useCallback(async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            setEmailVerified(auth.currentUser.emailVerified);
            return auth.currentUser.emailVerified;
        }
        return false;
    }, []);

    // Cross-app SSO (BBD-50): codes are issued on demand, not kept warm on a timer — the old
    // 30s refresh loop meant every open tab wrote to the (serverless, scale-to-zero) database
    // twice a minute around the clock. Call warmSSOCode() on a signal of intent to leave
    // (e.g. opening a menu that holds suite links); the 45s server-side TTL covers the click
    // that follows, and appendSSOCode falls back to a plain URL if no fresh code is on hand.
    useEffect(() => {
        if (!user) ssoCodeRef.current = { code: null, expiresAt: 0 };
    }, [user]);

    const warmSSOCode = useCallback(async () => {
        if (!user) return;
        const { code, expiresAt } = ssoCodeRef.current;
        if (code && Date.now() < expiresAt) return;
        if (!ssoInflightRef.current) {
            ssoInflightRef.current = (async () => {
                try {
                    const res = await apiCall('/users/sso/issue/', { method: 'POST' });
                    if (!res.ok) return;
                    const { code: newCode, expires_in } = await res.json();
                    ssoCodeRef.current = { code: newCode, expiresAt: Date.now() + expires_in * 1000 - 5000 };
                } catch {
                    // Best-effort — the outbound link just won't carry SSO this time.
                } finally {
                    ssoInflightRef.current = null;
                }
            })();
        }
        return ssoInflightRef.current;
    }, [user, apiCall]);

    const appendSSOCode = useCallback((url) => {
        const { code, expiresAt } = ssoCodeRef.current;
        if (!code || Date.now() > expiresAt) return url;
        const target = new URL(url);
        target.searchParams.set('sso', code);
        return target.toString();
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, token, emailVerified, getToken, apiCall, refreshEmailVerified, appendSSOCode, warmSSOCode }}>
            {children}
        </AuthContext.Provider>
    );
};
